import { forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useDropdownContext } from '~/components/Dropdown/hooks/useDropdownContext';
import { Icon } from '~/components/Icon';
import { cn } from '~/lib/cn';
import type { VariantProps } from '~/lib/variants';
import { variants } from '~/lib/variants';

import { useTriggerEventHandlers } from '../../hooks/useTriggerEventHandlers';
import { DropdownTag } from '../DropdownTag';

const MAX_DISPLAY_ROWS = 3;

const trigger = variants({
  base: [
    'w-full',
    'h-auto',
    'outline',
    'outline-1',
    'outline-emphasis-subtle',
    'outline-offset-[-1px]',
    'focus:ring-none',
    'text-body-sm',
    'rounded-xs',
    'py-sm',
    'pl-md',
    'pr-xl',
    'bg-emphasis-default',
    'text-strong',
    'cursor-pointer',
    'flex',
    'flex-wrap',
    'items-center',
    'justify-between',
    'gap-sm',
    'relative',
  ],
  variants: {
    state: {
      default: [
        'focus:outline-secondary-default',
        'focus:outline-2',
        'focus:outline-offset-[-2px]',
      ],
      error: [
        'outline',
        'outline-error-default',
        'outline-2',
        'outline-offset-[-2px]',
      ],
      warning: [
        'outline',
        'outline-warning-default',
        'outline-2',
        'outline-offset-[-2px]',
      ],
      success: [
        'outline',
        'outline-success-default',
        'outline-2',
        'outline-offset-[-2px]',
      ],
    },
    size: {
      xlarge: ['py-md'],
      large: ['min-h-[3rem]'],
    },
    open: {
      true: ['outline-2', 'outline-secondary-default', 'outline-offset-[-2px]'],
    },
    disabled: {
      true: [
        'outline-0',
        'cursor-not-allowed',
        'pointer-events-none',
        'text-disabled',
      ],
    },
  },
});

const chevron = variants({
  base: [
    'mx-sm',
    'transition-transform',
    'duration-200',
    'absolute',
    'right-sm',
  ],
  variants: {
    state: {
      default: ['transform', 'rotate-0'],
      open: ['transform', 'rotate-180'],
    },
  },
});

type DropdownTriggerProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof trigger> & {
    placeholder?: string;
    name?: string;
    label?: string;
    onClose?: () => void;
    clearable?: boolean;
  };

/** Safely derive what to show on the trigger for a selected option */
function getSelectedNode(opt: {
  renderSelected?:
    | React.ReactNode
    | ((args: { disabled: boolean }) => React.ReactNode);
  label: string;
  ariaLabel?: string;
  value: string;
  disabled?: boolean;
}) {
  if (typeof opt.renderSelected === 'function') {
    return opt.renderSelected({ disabled: Boolean(opt.disabled) });
  }

  if (opt.renderSelected != null) {
    return opt.renderSelected;
  }

  if (opt.label != null) {
    return opt.label;
  }

  return opt.ariaLabel ?? String(opt.value);
}

function DropdownTriggerWithoutRef(
  {
    size = 'large',
    state = 'default',
    disabled,
    placeholder,
    className,
    name,
    label,
    onClose,
    clearable,
    ...props
  }: DropdownTriggerProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const { t } = useTranslation('components', { keyPrefix: 'dropdown' });
  const {
    multiselect,
    displaySelectedOptions,
    isOpen,
    selected,
    onSelect,
    limitDisplayRows,
  } = useDropdownContext();

  const triggerHandlers = useTriggerEventHandlers(disabled, onClose);

  const singleSelectedNode = useMemo(() => {
    if (multiselect) {
      return null;
    }

    const first = selected[0];

    if (!first) {
      return null;
    }

    return getSelectedNode(first);
  }, [multiselect, selected]);

  return (
    <div
      className={cn([
        trigger({ size, state, disabled, open: isOpen }),
        className,
        multiselect && 'pr-2xl',
      ])}
      aria-disabled={disabled}
      aria-expanded={isOpen}
      id={name}
      role="combobox"
      tabIndex={0}
      {...props}
      {...triggerHandlers}
      ref={ref}
      onBlur={() => {
        if (isOpen) {
          onClose?.();
        }
      }}
      aria-label={label || props['aria-label']}
    >
      {/* Multiselect with tags that can remove selection (each chip can be JSX) */}
      {multiselect && displaySelectedOptions ? (
        <div
          className="scrollbar flex w-full flex-wrap gap-sm overflow-y-auto py-xs pl-px pr-xl"
          style={{
            maxHeight: limitDisplayRows
              ? `calc(${MAX_DISPLAY_ROWS} * (2rem + 1.5px))`
              : 'auto',
          }}
        >
          {selected.map((opt) => (
            <DropdownTag
              key={opt.value}
              onRemove={() => onSelect(opt.value)}
              disabled={disabled}
            >
              {getSelectedNode(opt)}
            </DropdownTag>
          ))}
          {selected.length === 0 ? (
            <span className="text-body-sm leading-6 text-subtle">
              {placeholder}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Multiselect with single non-interactive tag showing count */}
      {multiselect && !displaySelectedOptions && selected.length > 0 ? (
        <DropdownTag disabled={disabled}>
          {t('numberSelected', { count: selected.length })}
        </DropdownTag>
      ) : null}

      {/* Single select: show rich selected node */}
      {!multiselect && (
        <>
          {singleSelectedNode}
          {clearable && selected.length > 0 && !disabled ? (
            <button
              className="mr-sm"
              onClick={(e) => (
                onSelect(selected[0].value),
                e.stopPropagation()
              )}
            >
              <Icon type="xmark" size="md" aria-label="clear" />
            </button>
          ) : null}
        </>
      )}

      {/* Placeholder when nothing selected */}
      {selected.length === 0 && !multiselect && !displaySelectedOptions && (
        <span className="text-body-sm text-subtle">{placeholder}</span>
      )}

      <Icon
        className={chevron({ state: isOpen ? 'open' : 'default' })}
        size="md"
        type="chevron-down"
      />
    </div>
  );
}

DropdownTriggerWithoutRef.displayName = 'DropdownTriggerWithoutRef';

export const DropdownTrigger = forwardRef(DropdownTriggerWithoutRef);

DropdownTrigger.displayName = 'DropdownTrigger';
